import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { debounce } from 'es-toolkit';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Dialog, InputAdornment, Stack, TextField } from '@mui/material';
import { Search, Settings } from '@mui/icons-material';

import { siteSubscriptionsAtom } from '@/interfaces/subscription';
import { timeZoneAtom } from '@/interfaces/utils';
import { compareInLowerCase } from '@/components/common/utils';
import ManageSubscriptionDialog from '@/components/devices/ManageSubscriptionDialog';

import type { ChangeEvent } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import type { SiteSubscriptionsRow } from '@/@types/device';

export default function DeviceSubscriptions() {
  const { data: subscriptions, refetch } = useAtomValue(siteSubscriptionsAtom);
  const timeZone = useAtomValue(timeZoneAtom);

  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<SiteSubscriptionsRow[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<SiteSubscriptionsRow>();
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (subscriptions === undefined) return;
    setRows(subscriptions);
  }, [subscriptions]);

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const onSettingsClick = useCallback((device: SiteSubscriptionsRow) => {
    setOpen(true);
    setSelectedDevice(device);
  }, []);
  const columns = useMemo<GridColDef<SiteSubscriptionsRow>[]>(
    () => [
      { field: 'name', headerName: t('Common.label.name'), flex: 1, sortable: false },
      { field: 'serialNumber', headerName: t('Devices.label.serialNumber'), flex: 1, sortable: false },
      { field: 'plan', headerName: t('Devices.label.serviceType'), flex: 1, sortable: false },
      {
        field: 'expiredAt',
        type: 'dateTime',
        headerName: t('Devices.label.subscriptionExpires'),
        flex: 1,
        sortable: true,
        valueFormatter: (value: string) => {
          if (!value) return '';
          return format(value, timeFormat, { in: tz(timeZone) });
        },
      },
      { field: 'smsCount', headerName: t('Devices.label.remainingSMS'), flex: 1 },
      {
        field: 'settings',
        type: 'actions',
        headerName: t('Common.label.settings'),
        flex: 0.5,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem icon={<Settings />} onClick={() => onSettingsClick(params.row)} label="setting" />,
        ],
      },
    ],
    [onSettingsClick, t, timeFormat, timeZone],
  );

  const filterDevices = useMemo(
    () =>
      debounce((query: string) => {
        if (query === '') {
          setRows(subscriptions);
        } else {
          setRows(
            subscriptions.filter(
              (row) => compareInLowerCase(row.serialNumber, query) || compareInLowerCase(row.name, query),
            ),
          );
        }
      }, 500),
    [subscriptions],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterDevices(value);
  }
  const onDialogClose = useCallback(
    (reason?: string) => {
      setOpen(false);
      if (reason === 'updated') {
        refetch();
      }
    },
    [refetch],
  );

  return (
    <>
      <Stack gap={2} sx={{ flexGrow: 1, overflow: 'auto', pt: 2 }}>
        <TextField
          placeholder={t('Common.label.search')}
          value={query}
          onChange={onQueryChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: '300px', mr: 'auto', bgcolor: 'white' }}
        />

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>
      <Dialog open={open} onClose={onDialogClose} sx={{ '& .MuiPaper-root': { maxWidth: 'unset' } }}>
        <ManageSubscriptionDialog
          sensorId={selectedDevice?.id}
          planName={selectedDevice?.plan}
          onClose={onDialogClose}
        />
      </Dialog>
    </>
  );
}
